import { useState } from "react";
import { Plus, Trash2, Save, Star } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, imgSrc } from "@/lib/api";
import { useAdminContent, AdminTitle } from "./shared";
import ImageField from "./ImageField";

const EMPTY = { name: "", category: "Passed Hors d'oeuvres", description: "", price: "", image: "", featured: false, order: 99 };

export default function MenusManager() {
  const { content, reload } = useAdminContent();
  const [draft, setDraft] = useState(null);

  if (!content) return <p className="text-clay">Loading…</p>;
  const items = content.menu.map((d) => d.staging || d.live).filter(Boolean);
  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))];

  const save = async () => {
    try {
      await api.post("/admin/menu", draft);
      toast.success("Menu item saved to staging");
      setDraft(null);
      reload();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this dish?")) return;
    await api.delete(`/admin/menu/${id}`);
    toast.success("Dish deleted");
    reload();
  };

  return (
    <div data-testid="menus-manager">
      <AdminTitle eyebrow="The Food" title="Menu Items">
        <button onClick={() => setDraft({ ...EMPTY })} data-testid="menu-add-button" className="flex items-center gap-2 rounded-full bg-brass px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-cast hover:bg-flame hover:text-cream transition-colors">
          <Plus size={14} /> Add Dish
        </button>
      </AdminTitle>
      <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="space-y-8">
          {categories.map((cat) => (
            <div key={cat}>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass mb-3">{cat}</p>
              <div className="rounded-sm border border-brass/10 bg-card divide-y divide-border">
                {items.filter((i) => i.category === cat).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item) => (
                  <div key={item.id} className="flex items-center gap-4 px-5 py-3" data-testid={`menu-row-${item.id}`}>
                    {item.image && <img src={imgSrc(item.image)} alt={item.name} className="h-10 w-14 rounded-sm object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-cream truncate">{item.name} {item.featured && <Star size={12} className="inline fill-brass text-brass ml-1" />}</p>
                      <p className="text-xs text-clay truncate">{item.description}</p>
                    </div>
                    <button onClick={() => setDraft({ ...item })} data-testid={`menu-edit-${item.id}`} className="text-xs text-brass hover:underline">Edit</button>
                    <button onClick={() => remove(item.id)} data-testid={`menu-delete-${item.id}`} className="text-flame"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div>
          {draft ? (
            <div className="rounded-sm border border-brass/10 bg-card p-6 space-y-4 sticky top-24" data-testid="menu-edit-form">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass">{draft.id ? "Edit Dish" : "New Dish"}</p>
              <div><label className="admin-label">Name</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="admin-input" data-testid="menu-name-input" /></div>
              <div>
                <label className="admin-label">Category — decides which section it appears under online</label>
                <select
                  value={categories.includes(draft.category) ? draft.category : "__new"}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value === "__new" ? "" : e.target.value })}
                  className="admin-input"
                  data-testid="menu-category-select"
                >
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value="__new">+ New category…</option>
                </select>
                {!categories.includes(draft.category) && (
                  <input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="New category name" className="admin-input mt-2" data-testid="menu-category-input" />
                )}
              </div>
              <div><label className="admin-label">Description</label><textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="admin-input" data-testid="menu-description-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="admin-label">Price (optional)</label><input value={draft.price || ""} onChange={(e) => setDraft({ ...draft, price: e.target.value })} className="admin-input" data-testid="menu-price-input" /></div>
                <div><label className="admin-label">Sort Order</label><input type="number" value={draft.order ?? 99} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} className="admin-input" data-testid="menu-order-input" /></div>
              </div>
              <div>
                <label className="admin-label">Photo</label>
                <ImageField value={draft.image} onChange={(v) => setDraft({ ...draft, image: v })} testid="menu-image" />
              </div>
              <label className="flex items-center gap-3 text-sm text-clay cursor-pointer">
                <input type="checkbox" checked={!!draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} data-testid="menu-featured-checkbox" className="accent-brass" />
                Featured — shows large, double-width on the site
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={save} disabled={!draft.name} data-testid="menu-save-button" className="flex items-center gap-2 rounded-full bg-brass px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-cast hover:bg-flame hover:text-cream transition-colors disabled:opacity-40">
                  <Save size={14} /> Save
                </button>
                <button onClick={() => setDraft(null)} data-testid="menu-cancel-button" className="text-xs text-clay hover:text-cream">Cancel</button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-clay" data-testid="menu-edit-empty">Select a dish to edit, or add a new one. Categories are created just by typing a new category name — layout is controlled by Sort Order and the Featured flag.</p>
          )}
        </div>
      </div>
    </div>
  );
}
